import { BriefcaseIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { API_CALLS as JOB_OPPORTUNITIES_API } from '~/features/provider-hlx/job-opportunities/api';
import type { JobOpportunity } from '~/features/provider-hlx/job-opportunities/types';
import { paths } from '~/routing';
import { getHlxNavIcon } from '~/routing/hlxNavIcons';

// [MCV-1516] Match the left sidebar's icon for this destination. This card
// and UpcomingJobsCard both navigate to paths.hlx.jobOpportunities, so
// sharing BriefcaseIcon correctly signals they lead to the same place.
export const NegotiationsIcon = getHlxNavIcon(paths.hlx.jobOpportunities) ?? BriefcaseIcon;

export const ActiveNegotiationsCard = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<JobOpportunity[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    JOB_OPPORTUNITIES_API.getOpportunities('active')
      .then(({ items }) => setOpportunities(items))
      .catch(() => setOpportunities(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-grey-900 p-6 animate-pulse">
        <div className="h-5 bg-grey-900 rounded w-1/3 mb-4" />
        <div className="grid grid-cols-3 gap-4">
          <div className="h-12 bg-grey-900 rounded" />
          <div className="h-12 bg-grey-900 rounded" />
          <div className="h-12 bg-grey-900 rounded" />
        </div>
      </div>
    );
  }

  if (!opportunities) return null;

  const open = opportunities.filter((o) => o.status === 'NEGOTIATING' || o.status === 'APPLIED');
  const needsAction = open.filter(
    (o) => o.latestNegotiation == null || o.latestNegotiation.sender === 'EMPLOYER'
  ).length;
  const pending = open.filter((o) => o.latestNegotiation?.sender === 'PROVIDER').length;

  return (
    <button
      type="button"
      className="bg-white rounded-xl shadow-sm border border-grey-900 p-6 w-full text-left hover:shadow-md transition-shadow"
      onClick={() => navigate(paths.hlx.jobOpportunities)}
    >
      <div className="flex items-center gap-2 mb-4">
        <NegotiationsIcon className="size-5 text-primary-50" />
        <h3 className="sub-heading-sb text-grey-100">Active Negotiations</h3>
        <span className="ml-auto caption-text text-primary-50">View all →</span>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-2xl font-bold text-grey-100">{open.length}</p>
          <p className="body-text text-grey-500">Open</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-warning">{needsAction}</p>
          <p className="body-text text-grey-500">Needs action</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-grey-300">{pending}</p>
          <p className="body-text text-grey-500">Pending</p>
        </div>
      </div>
    </button>
  );
};
